import { USER_LOGOUT_CLEAR } from 'constants/ActionTypes';
import * as authTypes from 'modules/auth/actionTypes';
import { LOGIN_STEPS } from 'modules/auth/constant';
import { getDefaultUserId, getUserIdFromAuthSession } from 'modules/auth/utils';
import { PATCH_CONFIGURATION_SUCCESS, SUBSCRIBE_SUCCESS } from 'modules/notifications/actionTypes';
import * as usersTypes from 'modules/users/actionTypes';
import { sortObjectByKeys } from 'utils/global';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {import('../../types/currentUser').CurrentUserData} CurrentUserData
 */

/** @type {CurrentUserData} */
const initialState = {};

/**
 * Format notification config by replacing dots with underscores
 * @param {Object} notificationConfig
 * @returns {Object}
 */
const formatNotificationConfig = (notificationConfig = {}) =>
  sortObjectByKeys(
    Object.entries(notificationConfig).reduce(
      (acc, [notificationSetting, value]) => ({
        ...acc,
        [notificationSetting.replace('.', '_')]: value,
      }),
      {}
    )
  );

/**
 * Handle sign in success
 * @param {CurrentUserData} state
 * @param {import('modules/auth/types').SignInSuccessAction} action
 * @returns {CurrentUserData}
 */
const handleSignInSuccess = (state, action) => {
  const { payload: token } = action.payload.idToken;
  const userId = getUserIdFromAuthSession(action.payload);
  const originUserId = getDefaultUserId(action.payload);

  return {
    ...state,
    userId,
    originUserId,
    groupId: token.group_id,
    username: token.user_name,
    dealerIds: token.dealer_ids,
    extensions: token.extensions,
    roles: token.roles.split(',').join(','),
    jwtToken: action.payload.idToken.toString(),
  };
};

/**
 * Handle load session success
 * @param {CurrentUserData} state
 * @param {import('modules/auth/types').LoadSessionSuccessAction} action
 * @returns {CurrentUserData}
 */
const handleLoadSessionSuccess = (state, { payload }) => {
  const { payload: token } = payload.idToken;
  const userId = getUserIdFromAuthSession(payload);
  const originUserId = getDefaultUserId(payload);

  return {
    ...state,
    userId,
    originUserId,
    username: token.user_name,
    extensions: token.extensions,
    roles: token.roles.split(',').join(','),
    jwtToken: payload.idToken.toString(),
  };
};

/**
 * Handle current user data success
 * @param {CurrentUserData} state
 * @param {import('modules/users/types/actions').GetCurrentUserDataSuccessAction} action
 * @returns {CurrentUserData}
 */
const handleCurrentUserDataSuccess = (state, { payload }) => ({
  ...state,
  additionalData: {
    ...payload,
    notificationConfig: formatNotificationConfig(payload.notificationConfig),
    roles: payload.roles,
  },
});

/**
 * Handle notification config patch success
 * @param {CurrentUserData} state
 * @param {import('modules/notifications/types').PatchConfigurationSuccessAction} action
 * @returns {CurrentUserData}
 */
const handlePatchConfigurationSuccess = (state, { payload }) => ({
  ...state,
  additionalData: {
    ...state.additionalData,
    notificationConfig: formatNotificationConfig(payload.configuration),
  },
});

/**
 * Handle subscribe success (push notification token)
 * @param {CurrentUserData} state
 * @param {import('modules/notifications/types').SubscribeActionSuccess} action
 * @returns {CurrentUserData}
 */
const handleSubscribeSuccess = (state, action) => {
  if (state.userId === action.payload.userId) {
    return {
      ...state,
      additionalData: {
        ...state.additionalData,
        token: action.payload.token,
      },
    };
  }
  return state;
};

/**
 * Handle patch user success
 * @param {CurrentUserData} state
 * @param {import('modules/users/types/actions').PatchUserSuccessAction} action
 * @returns {CurrentUserData}
 */
const handlePatchUserSuccess = (state, action) => {
  if (state.userId === action.payload.id) {
    return {
      ...state,
      additionalData: {
        ...(state.additionalData ?? {}),
        ...action.payload,
      },
    };
  }
  return state;
};

/**
 * @param {CurrentUserData} state
 * @param {import('modules/auth/types').SetAuthStepAction} action
 * @returns {CurrentUserData}
 */
const handleSetAuthStep = (state, { type, payload }) => {
  if (type !== authTypes.SET_AUTH_STEP || payload.step === LOGIN_STEPS.LOGIN) {
    return initialState;
  }
  return state;
};

/**
 * Handle sign out / clear data
 * @returns {CurrentUserData}
 */
const handleClearData = () => ({});

/**
 * Handle optimistic update of dashboard field
 * @param {CurrentUserData} state
 * @param {import('modules/users/types/dashboardPreferences').OptimisticUpdateDashboardFieldAction} action
 * @returns {CurrentUserData}
 */
const handleOptimisticDashboardFieldUpdate = (state, { payload: { dashboardType, hiddenFields } }) => ({
  ...state,
  additionalData: {
    ...state.additionalData,
    displaySettings: {
      ...state.additionalData?.displaySettings,
      dashboard: {
        ...state.additionalData?.displaySettings?.dashboard,
        [dashboardType]: { hiddenFields },
      }
    },
  },
});

const handleUpdateDashboardSettings = (state, { payload: { dashboard } }) => ({
  ...state,
  additionalData: {
    ...state.additionalData,
    displaySettings: {
      ...state.additionalData?.displaySettings,
      dashboard: {
        ...state.additionalData?.displaySettings?.dashboard,
        ...dashboard,
      }
    },
  },
});

export default createReducer(initialState, {
  // Reset on logout or auth step change
  [USER_LOGOUT_CLEAR]: handleSetAuthStep,
  [authTypes.SET_AUTH_STEP]: handleSetAuthStep,
  [authTypes.SIGN_OUT_EMERGENCY]: handleSetAuthStep,
  [authTypes.SIGN_OUT_REQUEST]: handleClearData,
  [authTypes.SIGN_OUT_FAILURE]: handleClearData,

  [SUBSCRIBE_SUCCESS]: handleSubscribeSuccess,
  [authTypes.SIGN_IN_SUCCESS]: handleSignInSuccess,
  [usersTypes.PATCH_USER_SUCCESS]: handlePatchUserSuccess,
  [authTypes.LOAD_SESSION_SUCCESS]: handleLoadSessionSuccess,
  [PATCH_CONFIGURATION_SUCCESS]: handlePatchConfigurationSuccess,
  [usersTypes.CURRENT_USER_DATA_SUCCESS]: handleCurrentUserDataSuccess,

  // Dashboard customization
  [usersTypes.PATCH_DASHBOARD_SETTINGS_SUCCESS]: handleUpdateDashboardSettings,
  [usersTypes.ROLLBACK_DASHBOARD_FIELDS]: handleOptimisticDashboardFieldUpdate,
  [usersTypes.OPTIMISTIC_UPDATE_DASHBOARD_FIELD]: handleOptimisticDashboardFieldUpdate,
});
